import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/stores/auth'
import { logger } from '@/lib/logger'

interface SwitchCompanyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Company switcher for tenant admins / collections agents who belong to more
 * than one tenant ("one login, multiple companies"). Mirrors the super admin's
 * SwitchTenantViewDialog, but sources the list from the user's own memberships
 * (auth store `tenants`) and updates the per-tenant role on switch.
 */
export function SwitchCompanyDialog({ open, onOpenChange }: SwitchCompanyDialogProps) {
  const tenants = useAuthStore((s) => s.tenants)
  const activeTenantId = useAuthStore((s) => s.activeTenantId)
  const switchTenant = useAuthStore((s) => s.switchTenant)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const handleSwitch = (tenantId: string) => {
    if (tenantId === activeTenantId) {
      onOpenChange(false)
      return
    }
    if (!switchTenant(tenantId)) {
      // Switch was rejected (no longer a member of this company). Surface it
      // instead of navigating to a brand the session can't actually act on.
      logger.error('SwitchCompany', 'Could not switch company — you may no longer have access. Try refreshing.')
      onOpenChange(false)
      return
    }
    // Refetch tenant-scoped data + module_config under the new X-Tenant-ID header.
    queryClient.invalidateQueries()
    navigate(`/${tenantId}/dashboard`)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Switch Company</DialogTitle>
          <DialogDescription>Select a company to work in</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-4 max-h-80 overflow-y-auto">
          {tenants.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => handleSwitch(t.id)}
              className={`w-full flex items-center justify-between p-3 rounded-md border transition-colors text-left hover:bg-accent cursor-pointer ${
                t.id === activeTenantId ? 'border-primary' : ''
              }`}
            >
              <span className="font-medium">{t.name}</span>
              <Badge variant={t.id === activeTenantId ? 'success' : 'secondary'} className="capitalize">
                {t.id === activeTenantId ? 'Current' : t.role.replace('_', ' ')}
              </Badge>
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
